/**
 * PupRing Custom JavaScript - Bundle Builder Upsells Module
 * ==========================================================
 * Buy 3 for $99 Bundle Builder - Upsells & Summary Steps
 *
 * Features:
 * - BundleUpsellsManager: Gift box selection, gift note, warranty toggle
 * - BundleSummaryManager: Order review with pricing breakdown
 * - Schema-driven product configuration
 * - Phase 8 cart integration ready
 *
 * Dependencies:
 * - custom-bundle-state.js (BundleStateManager)
 * - custom-bundle-ui.js (BundleUIManager)
 * - custom-bundle-router.js (loads this module)
 */

// ═══════════════════════════════════════════════════════════════════════════════
// BUNDLE UPSELLS MANAGER
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * BundleUpsellsManager - Handles the upsells step
 *
 * Responsibilities:
 * - Gift box quantity selection (0-3)
 * - Gift box type selection (schema-driven)
 * - Gift note textarea (350 char limit)
 * - Warranty toggle
 * - Navigation to summary step
 */
class BundleUpsellsManager {
  // ═══════════════════════════════════════════════════════════════
  // CONSTRUCTOR
  // ═══════════════════════════════════════════════════════════════

  constructor(stateManager, uiManager) {
    this.stateManager = stateManager;
    this.uiManager = uiManager;
    this.container = null;
    this.upsellProducts = null;

    // Bound handlers for cleanup
    this.boundClickHandler = null;
    this.boundInputHandler = null;
  }

  // ═══════════════════════════════════════════════════════════════
  // INITIALIZATION
  // ═══════════════════════════════════════════════════════════════

  /**
   * Initialize the upsells step
   * Wrapped in try-catch for consistent error handling
   */
  init() {
    try {
      // Find container
      this.container = document.getElementById('bundle-upsells-step');
      if (!this.container) {
        throw new Error('Container #bundle-upsells-step not found');
      }

      // Use container directly, no card-body needed
      this.targetElement = this.container;

      // Load product data (throws on critical failure)
      this.loadUpsellProducts();

      // Render content
      this.render();

      // Bind events
      this.bindEvents();
    } catch (error) {
      console.error('[BundleUpsells] Initialization failed:', error.message);
      this.renderErrorState(error.message);
    }
  }

  /**
   * Load upsell products data from JSON script tag
   * Falls back gracefully to empty state if data unavailable
   */
  loadUpsellProducts() {
    // Default empty state
    const defaultProducts = { giftBoxes: [], warranty: null, giftNote: null };
    const defaultTitle = 'Enhance Your Bundle';

    const dataEl = document.getElementById('bundle-upsells-data');
    if (!dataEl) {
      // Data element missing - use defaults (no upsells configured)
      this.upsellProducts = defaultProducts;
      this.upsellsTitle = defaultTitle;
      return;
    }

    try {
      this.upsellProducts = JSON.parse(dataEl.textContent);
      this.upsellsTitle = this.upsellProducts.upsellsTitle || defaultTitle;
    } catch (e) {
      // JSON parse failed - use defaults
      console.error('[BundleUpsells] JSON parse failed:', e.message);
      this.upsellProducts = defaultProducts;
      this.upsellsTitle = defaultTitle;
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // RENDERING
  // ═══════════════════════════════════════════════════════════════

  /**
   * Render the upsells step content
   */
  render() {
    const state = this.stateManager.get();

    // Use targetElement which is the container itself
    const target = this.targetElement || this.container;
    if (!target) {
      console.error('[BundleUpsells] Cannot render - no target element');
      return;
    }

    // Pre-render sections to check if any have content
    const giftBoxHtml = this.renderGiftBoxSection(state.upsells);
    const giftNoteHtml = this.renderGiftNoteSection(state.upsells);
    const warrantyHtml = this.renderWarrantySection(state.upsells);
    const hasAnyOptions = giftBoxHtml || giftNoteHtml || warrantyHtml;

    // Use new title and add subtitle
    const upsellsTitle = "Make This the BEST Gift She's Ever Received";
    const subtitle = "Select your gift packaging (optional)";

    target.innerHTML = `
      <div class="bundle-upsells-content">
        <h4 class="bundle-step-title mb-2 text-center">${upsellsTitle}</h4>
        <p class="bundle-step-subtitle text-muted text-center mb-4">${subtitle}</p>
        ${hasAnyOptions ? `
          ${giftBoxHtml}
          ${giftNoteHtml}
          ${warrantyHtml}
        ` : `
          <p class="text-muted text-center py-4">
            No additional options available for this bundle.
          </p>
        `}
        ${this.renderNavigation()}
      </div>
    `;

    // Verify render succeeded
    if (!target.querySelector('.bundle-upsells-content')) {
      console.error('[BundleUpsells] Render failed - content not in DOM after innerHTML');
    }
  }

  /**
   * Render gift box type selection
   * Shows all configured boxes; unavailable boxes display "Out of Stock" indicator
   */
  renderGiftBoxSection(upsells) {
    const allBoxes = this.upsellProducts?.giftBoxes || [];
    const availableBoxes = allBoxes.filter(b => b.available);

    // Only hide section if NO boxes configured at all
    if (allBoxes.length === 0) {
      return '';
    }

    // Get current values with defaults
    // When a box is selected, we'll use quantity of 3 (one for each product)
    const quantity = upsells?.giftBoxQuantity ?? 0;
    const selectedType = upsells?.giftBoxType || null;

    // Check if ALL boxes are out of stock
    const allOutOfStock = availableBoxes.length === 0;

    return `
      <div class="bundle-upsell-section">
        <div class="bundle-upsell-header d-flex align-items-center justify-content-between mb-3">
          <span>Gift Box</span>
          ${selectedType && availableBoxes.find(b => b.id === selectedType) ? `
            <small class="text-success">✓ Added</small>
          ` : ''}
        </div>

        ${allOutOfStock ? `
          <div class="alert alert-warning py-2 mb-3">
            <small>⚠️ Gift boxes are currently out of stock</small>
          </div>
        ` : `
          <div class="bundle-gift-box-vertical">
            ${allBoxes.map(box => `
              <div
                class="bundle-gift-box-option-vertical ${box.id === selectedType && quantity > 0 ? 'selected' : ''} ${!box.available ? 'out-of-stock' : ''}"
                data-bundle-box-type="${box.id}"
                ${!box.available ? 'data-unavailable="true"' : ''}
              >
                <div class="bundle-gift-box-option-content">
                  ${box.image ? `<img src="${BundleUpsellsManager.escapeHtml(box.image)}" alt="${BundleUpsellsManager.escapeHtml(box.title)}" class="bundle-gift-box-image">` : ''}
                  <div class="bundle-gift-box-info">
                    <div class="bundle-gift-box-title">${BundleUpsellsManager.escapeHtml(box.title)}</div>
                    ${box.available ? `
                      <div class="bundle-gift-box-price text-muted small">${this.formatPrice(box.price)}</div>
                    ` : '<span class="badge bg-secondary">Out of Stock</span>'}
                  </div>
                  ${box.available ? `
                    <button
                      type="button"
                      class="btn ${box.id === selectedType && quantity > 0 ? 'btn-success' : 'btn-primary'} btn-sm bundle-gift-box-add-btn"
                      data-bundle-box-add="${box.id}"
                    >
                      ${box.id === selectedType && quantity > 0 ? '✓ Added' : 'Add 3'}
                    </button>
                  ` : ''}
                </div>
              </div>
            `).join('')}
          </div>
        `}
      </div>
    `;
  }

  /**
   * Render gift note textarea section with toggle
   */
  renderGiftNoteSection(upsells) {
    const giftNoteProduct = this.upsellProducts?.giftNote;

    // If no gift note product configured, return empty
    if (!giftNoteProduct) {
      return '';
    }

    const noteValue = upsells?.giftNote || '';
    const hasNote = noteValue.trim().length > 0;
    const notePrice = this.formatPrice(giftNoteProduct.price);

    return `
      <div class="bundle-upsell-section bundle-gift-note-section">
        <div class="bundle-gift-note-box">
          <div class="bundle-gift-note-header">
            <div class="bundle-gift-note-info">
              <div class="bundle-gift-note-title">Add a custom gift note to this order</div>
              <div class="bundle-gift-note-price">${notePrice}</div>
            </div>
            <div class="form-check form-switch bundle-gift-note-switch">
              <input
                class="form-check-input"
                type="checkbox"
                role="switch"
                id="bundle-gift-note-toggle"
                data-bundle-gift-note-toggle
                ${hasNote ? 'checked' : ''}
              >
            </div>
          </div>

          <div class="bundle-gift-note-content ${hasNote ? 'show' : ''}" data-bundle-gift-note-content>
            <textarea
              class="form-control bundle-gift-note-textarea"
              placeholder="Write your message here..."
              maxlength="350"
              data-bundle-gift-note
            >${BundleUpsellsManager.escapeHtml(noteValue)}</textarea>

            <div class="d-flex justify-content-end mt-1">
              <span class="bundle-gift-note-counter" data-gift-note-counter>${noteValue.length}/350</span>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Render warranty toggle section
   */
  renderWarrantySection(upsells) {
    const warrantyProduct = this.upsellProducts?.warranty;

    // If no warranty product configured, return empty
    if (!warrantyProduct) {
      return '';
    }

    const hasWarranty = upsells?.hasWarranty || false;
    const warrantyPrice = this.formatPrice(warrantyProduct.price);

    const warrantyImageUrl = warrantyProduct.image || '';
    const warrantyTitle = BundleUpsellsManager.escapeHtml(warrantyProduct.title || 'Lifetime Warranty');

    return `
      <div class="bundle-upsell-section">
        <div class="bundle-warranty-option-vertical ${hasWarranty ? 'selected' : ''}">
          <div class="bundle-warranty-option-content">
            <div class="bundle-warranty-icon">
              ${warrantyImageUrl ? `<img src="${BundleUpsellsManager.escapeHtml(warrantyImageUrl)}" alt="${warrantyTitle}" class="bundle-warranty-image">` : ''}
            </div>
            <div class="bundle-warranty-info">
              <div class="bundle-warranty-title">${warrantyTitle}</div>
              <div class="bundle-warranty-price text-muted">${warrantyPrice}</div>
              <div class="bundle-warranty-desc text-muted small">Covers all 3 pieces</div>
            </div>
            <div class="bundle-warranty-toggle-wrapper">
              <div class="form-check form-switch bundle-warranty-switch">
                <input
                  class="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="bundle-warranty-toggle"
                  data-bundle-warranty
                  ${hasWarranty ? 'checked' : ''}
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Render navigation buttons
   */
  renderNavigation() {
    return `
      <div class="bundle-upsells-nav d-flex gap-3 mt-4">
        <button type="button" class="btn btn-outline-secondary flex-fill" data-bundle-back>
          ← Back
        </button>
        <button type="button" class="btn btn-primary flex-fill" data-bundle-continue>
          Continue →
        </button>
      </div>
    `;
  }

  /**
   * Render error state when initialization fails
   * Binds navigation directly to avoid recursive bindEvents() call
   * @param {string} message - Error message to display
   */
  renderErrorState(message) {
    // Use targetElement or container directly
    const target = this.targetElement || this.container;
    if (!target) return;

    target.innerHTML = `
      <div class="bundle-upsells-content">
        <h4 class="bundle-step-title mb-4">${this.upsellsTitle || 'Enhance Your Bundle'}</h4>
        <div class="alert alert-warning text-center py-4">
          <p class="mb-0">Unable to load additional options.</p>
          <small class="text-muted">You can continue without add-ons.</small>
        </div>
        ${this.renderNavigation()}
      </div>
    `;

    // Bind navigation buttons directly (not via bindEvents to avoid recursion)
    const backBtn = target.querySelector('[data-bundle-back]');
    const continueBtn = target.querySelector('[data-bundle-continue]');

    if (backBtn) {
      backBtn.addEventListener('click', () => {
        const state = this.stateManager.get();
        const prevStep = state.customizationMode === 'together' ? 'together' : 'individual-3';
        this.stateManager.goToStep(prevStep);
      });
    }

    if (continueBtn) {
      continueBtn.addEventListener('click', () => {
        this.stateManager.goToStep('summary');
      });
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // EVENT HANDLING
  // ═══════════════════════════════════════════════════════════════

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Remove existing handlers first
    this.cleanup();

    // Click handler for buttons
    this.boundClickHandler = (e) => {
      // Gift box Add 3 button click
      const addBoxBtn = e.target.closest('[data-bundle-box-add]');
      if (addBoxBtn) {
        const boxId = addBoxBtn.dataset.bundleBoxAdd;
        this.handleBoxAddClick(boxId);
        return;
      }

      // Navigation
      if (e.target.closest('[data-bundle-back]')) {
        this.handleBack();
        return;
      }

      if (e.target.closest('[data-bundle-continue]')) {
        this.handleContinue();
        return;
      }
    };
    this.container.addEventListener('click', this.boundClickHandler);

    // Input handler for textarea and warranty toggle
    this.boundInputHandler = (e) => {
      // Gift note toggle switch
      if (e.target.closest('[data-bundle-gift-note-toggle]')) {
        this.handleGiftNoteToggle(e.target.checked);
        return;
      }

      // Gift note textarea
      if (e.target.closest('[data-bundle-gift-note]')) {
        this.handleNoteInput(e.target.value);
        return;
      }

      // Warranty toggle
      if (e.target.closest('[data-bundle-warranty]')) {
        this.handleWarrantyToggle(e.target.checked);
        return;
      }
    };
    this.container.addEventListener('input', this.boundInputHandler);
    this.container.addEventListener('change', this.boundInputHandler);
  }

  /**
   * Handle gift box Add 3 button click
   * If this box is currently selected, deselect it
   * If another box or no box is selected, select this box with quantity 3
   */
  handleBoxAddClick(boxId) {
    const currentUpsells = this.stateManager.get().upsells || {};
    const currentType = currentUpsells.giftBoxType;
    const currentQuantity = currentUpsells.giftBoxQuantity || 0;

    // Toggle logic:
    // If clicking the button for the currently selected box, deselect it
    // If clicking a button for a different box, select it with quantity 3
    if (currentType === boxId && currentQuantity > 0) {
      // Deselect - set quantity to 0
      this.stateManager.set({
        upsells: { ...currentUpsells, giftBoxType: null, giftBoxQuantity: 0 }
      });
    } else {
      // Select this box with quantity 3 (one for each product)
      this.stateManager.set({
        upsells: { ...currentUpsells, giftBoxType: boxId, giftBoxQuantity: 3 }
      });
    }

    // Re-render to update button states
    this.render();
    this.bindEvents();
  }

  /**
   * Handle gift note toggle switch
   */
  handleGiftNoteToggle(checked) {
    const contentArea = this.container.querySelector('[data-bundle-gift-note-content]');
    const textarea = this.container.querySelector('[data-bundle-gift-note]');

    if (checked) {
      // Show the textarea with animation
      if (contentArea) {
        contentArea.classList.add('show');
      }
      // Focus on the textarea for better UX
      if (textarea) {
        setTimeout(() => textarea.focus(), 300);
      }
    } else {
      // Hide the textarea and clear the content
      if (contentArea) {
        contentArea.classList.remove('show');
      }
      // Clear the gift note when toggle is off
      const currentUpsells = this.stateManager.get().upsells;
      this.stateManager.set({
        upsells: { ...currentUpsells, giftNote: '' }
      });
      // Clear the textarea visually
      if (textarea) {
        textarea.value = '';
      }
      // Update counter
      const counter = this.container.querySelector('[data-gift-note-counter]');
      if (counter) {
        counter.textContent = '0/350';
      }
    }
  }

  /**
   * Handle gift note input
   */
  handleNoteInput(value) {
    const truncated = value.slice(0, 350);
    const currentUpsells = this.stateManager.get().upsells;
    this.stateManager.set({
      upsells: { ...currentUpsells, giftNote: truncated }
    });

    // Update counter only, not full re-render
    const counter = this.container.querySelector('[data-gift-note-counter]');
    if (counter) {
      counter.textContent = `${truncated.length}/350`;
    }
  }

  /**
   * Handle warranty toggle
   */
  handleWarrantyToggle(checked) {
    const currentUpsells = this.stateManager.get().upsells;
    this.stateManager.set({
      upsells: { ...currentUpsells, hasWarranty: checked }
    });
  }

  /**
   * Handle continue button
   */
  handleContinue() {
    this.stateManager.goToStep('summary');
  }

  /**
   * Handle back button
   */
  handleBack() {
    this.stateManager.goBack();
  }

  // ═══════════════════════════════════════════════════════════════
  // UTILITIES
  // ═══════════════════════════════════════════════════════════════

  /**
   * Format price from cents to display string
   */
  formatPrice(cents) {
    return BundleUpsellsManager.formatMoney(cents);
  }

  /**
   * Format cents with the shop money format (HTML fragment; markup allowed)
   */
  static formatMoney(cents) {
    const format = window.theme?.moneyFormat || window.Shopify?.money_format;
    if (format && typeof window.Shopify?.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents || 0, format);
    }
    return `$${((cents || 0) / 100).toFixed(2)}`;
  }

  /**
   * Escape text for interpolation into HTML
   */
  static escapeHtml(value) {
    const entities = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return String(value ?? '').replace(/[&<>"']/g, char => entities[char]);
  }

  // ═══════════════════════════════════════════════════════════════
  // CLEANUP
  // ═══════════════════════════════════════════════════════════════

  /**
   * Cleanup event listeners
   */
  cleanup() {
    if (this.boundClickHandler) {
      this.container?.removeEventListener('click', this.boundClickHandler);
      this.boundClickHandler = null;
    }
    if (this.boundInputHandler) {
      this.container?.removeEventListener('input', this.boundInputHandler);
      this.container?.removeEventListener('change', this.boundInputHandler);
      this.boundInputHandler = null;
    }
  }
}

// Export for other modules
window.BundleUpsellsManager = BundleUpsellsManager;

// ═══════════════════════════════════════════════════════════════════════════════
// BUNDLE SUMMARY MANAGER
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * BundleSummaryManager - Handles the summary/review step
 *
 * Responsibilities:
 * - Display all 3 products with customization summary
 * - Show upsells selections
 * - Calculate and display pricing breakdown
 * - Edit buttons for navigation
 * - Complete Order button (Phase 8 placeholder)
 */
class BundleSummaryManager {
  // ═══════════════════════════════════════════════════════════════
  // CONSTANTS
  // ═══════════════════════════════════════════════════════════════

  static BUNDLE_PRICE = 9900; // $99.00 in cents
  static ENGRAVING_PRICE = 1000; // $10.00 per pendant in cents

  // ═══════════════════════════════════════════════════════════════
  // CONSTRUCTOR
  // ═══════════════════════════════════════════════════════════════

  constructor(stateManager, uiManager, productsData) {
    this.stateManager = stateManager;
    this.uiManager = uiManager;
    this.productsData = productsData; // Array of full product JSON from Shopify
    this.container = null;
    this.upsellProducts = null;

    // Bound handlers for cleanup
    this.boundClickHandler = null;
  }

  // ═══════════════════════════════════════════════════════════════
  // INITIALIZATION
  // ═══════════════════════════════════════════════════════════════

  /**
   * Initialize the summary step
   */
  init() {
    this.container = document.getElementById('bundle-summary-step');
    if (!this.container) return;

    this.loadUpsellProducts();
    this.render();
    this.bindEvents();
  }

  /**
   * Load upsell products data from JSON script tag
   */
  loadUpsellProducts() {
    const dataEl = document.getElementById('bundle-upsells-data');
    if (dataEl) {
      try {
        this.upsellProducts = JSON.parse(dataEl.textContent);
      } catch (e) {
        this.upsellProducts = { giftBoxes: [], warranty: null, giftNote: null };
      }
    } else {
      this.upsellProducts = { giftBoxes: [], warranty: null, giftNote: null };
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // RENDERING
  // ═══════════════════════════════════════════════════════════════

  /**
   * Render the summary step content
   */
  render() {
    const state = this.stateManager.get();
    const pricing = this.calculatePricing(state);
    const cardBody = this.container.querySelector('.card-body');

    cardBody.innerHTML = `
      <div class="bundle-summary-content">
        <h4 class="bundle-step-title mb-4">Review Your Bundle</h4>
        ${this.renderProductsSummary(state)}
        ${this.renderUpsellsSummary(state.upsells)}
        ${this.renderPricingBreakdown(pricing)}
        ${this.renderNavigation()}
      </div>
    `;
  }

  /**
   * Render products summary
   */
  renderProductsSummary(state) {
    if (!state.products || state.products.length === 0) {
      return '<p class="text-muted">No products selected</p>';
    }

    const productCards = state.products.map((product, index) => {
      return this.renderProductCard(product, index, state);
    }).join('');

    return `
      <div class="bundle-summary-products">
        ${productCards}
      </div>
    `;
  }

  /**
   * Render a single product card
   */
  renderProductCard(product, index, state) {
    // Get image
    const image = product.selectedVariantImage || product.featuredImage || '';
    const resizedImage = this.resizeImage(image, '80x80');

    // Build customization details
    const details = [];

    // Variant
    if (product.selectedVariantTitle && product.selectedVariantTitle !== 'Default Title') {
      details.push(BundleUpsellsManager.escapeHtml(product.selectedVariantTitle));
    }

    // Pet names (only for the pendants the selected variant has)
    const petNames = (product.customization?.petNames || []).slice(0, product.pendantCount || 1);
    if (petNames.length > 0) {
      const validNames = petNames.filter(n => n && n.trim());
      if (validNames.length > 0) {
        details.push(`Name: ${validNames.map(name => BundleUpsellsManager.escapeHtml(name)).join(', ')}`);
      }
    }

    // Photo
    if (product.customization?.photoUrl) {
      details.push('Photo uploaded ✓');
    }

    // Engraving
    const engravingCount = BundleSummaryManager.countEngravings(product);
    if (engravingCount > 0) {
      details.push(`Engraving (${engravingCount} pendant${engravingCount > 1 ? 's' : ''})`);
    }

    // Determine edit target step
    let editStep = 'selection';
    if (state.customizationMode === 'together') {
      editStep = 'together';
    } else {
      editStep = `individual-${index + 1}`;
    }

    return `
      <div class="bundle-summary-product">
        <img src="${BundleUpsellsManager.escapeHtml(resizedImage)}" alt="${BundleUpsellsManager.escapeHtml(product.productTitle)}" class="bundle-summary-thumbnail">
        <div class="bundle-summary-details">
          <h6 class="mb-1">${BundleUpsellsManager.escapeHtml(product.productTitle)}</h6>
          ${details.length > 0 ? `
            <p class="mb-0">${details.join(' · ')}</p>
          ` : ''}
        </div>
        <span class="bundle-summary-edit" data-bundle-edit="${editStep}">Edit</span>
      </div>
    `;
  }

  /**
   * Render upsells summary
   */
  renderUpsellsSummary(upsells) {
    const items = [];

    // Gift boxes (a quantity without a chosen box type is not a selection)
    const selectedBox = upsells?.giftBoxType
      ? this.upsellProducts?.giftBoxes?.find(b => b.id === upsells.giftBoxType)
      : null;
    if (upsells?.giftBoxQuantity > 0 && selectedBox) {
      const boxName = selectedBox.title || 'Gift Box';
      items.push(`${upsells.giftBoxQuantity}× ${BundleUpsellsManager.escapeHtml(boxName)}`);
    }

    // Gift note
    if (upsells?.giftNote?.trim()) {
      const notePreview = upsells.giftNote.slice(0, 30) + (upsells.giftNote.length > 30 ? '...' : '');
      items.push(`Gift Note: "${BundleUpsellsManager.escapeHtml(notePreview)}"`);
    }

    // Warranty
    if (upsells?.hasWarranty) {
      items.push('Lifetime Warranty ✓');
    }

    if (items.length === 0) {
      return '';
    }

    return `
      <div class="bundle-summary-upsells mb-3">
        <h6 class="text-muted small mb-2">ADD-ONS</h6>
        ${items.map(item => `
          <div class="d-flex align-items-center justify-content-between mb-1">
            <span class="small">${item}</span>
            <span class="bundle-summary-edit small" data-bundle-edit="upsells">Edit</span>
          </div>
        `).join('')}
      </div>
    `;
  }

  /**
   * Render pricing breakdown
   */
  renderPricingBreakdown(pricing) {
    return `
      <div class="bundle-pricing-breakdown">
        <div class="bundle-pricing-row">
          <span>Original Price:</span>
          <span class="bundle-pricing-original">${this.formatPrice(pricing.originalPrice)}</span>
        </div>
        <div class="bundle-pricing-row">
          <span>Bundle Price (3 for $99):</span>
          <span>${this.formatPrice(pricing.bundlePrice)}</span>
        </div>
        ${pricing.bundleDiscount > 0 ? `
          <div class="bundle-pricing-row bundle-pricing-discount">
            <span>You Save:</span>
            <span>-${this.formatPrice(pricing.bundleDiscount)}</span>
          </div>
        ` : ''}
        ${pricing.upsellsTotal > 0 ? `
          <div class="bundle-pricing-row">
            <span>Add-ons:</span>
            <span>+${this.formatPrice(pricing.upsellsTotal)}</span>
          </div>
        ` : ''}
        <div class="bundle-pricing-row bundle-pricing-total">
          <span>Total:</span>
          <span>${this.formatPrice(pricing.grandTotal)}</span>
        </div>
        ${pricing.totalSavings > 0 ? `
          <div class="text-center mt-3">
            <span class="bundle-savings-badge">You're saving ${this.formatPrice(pricing.totalSavings)}!</span>
          </div>
        ` : ''}
      </div>
    `;
  }

  /**
   * Render navigation buttons
   */
  renderNavigation() {
    return `
      <div class="bundle-summary-nav d-flex gap-3 mt-4">
        <button type="button" class="btn btn-outline-secondary flex-fill" data-bundle-back>
          ← Back
        </button>
        <button type="button" class="btn btn-primary flex-fill" data-bundle-complete>
          Complete Order
        </button>
      </div>
    `;
  }

  // ═══════════════════════════════════════════════════════════════
  // PRICING CALCULATION
  // ═══════════════════════════════════════════════════════════════

  /**
   * Calculate all pricing values
   */
  calculatePricing(state) {
    let originalPrice = 0;
    let engravingCount = 0;

    // Sum product prices from productsData
    state.products.forEach((product, index) => {
      const productData = this.productsData?.[index];
      if (productData) {
        const variant = productData.variants.find(v => v.id === product.selectedVariantId);
        if (variant) {
          // Use compare_at_price if available, otherwise regular price
          originalPrice += variant.compare_at_price || variant.price || 0;
        }
      }

      // Count engravings
      engravingCount += BundleSummaryManager.countEngravings(product);
    });

    // Upsell costs
    const upsells = state.upsells || {};

    // Gift boxes
    const selectedBox = this.upsellProducts?.giftBoxes?.find(b => b.id === upsells.giftBoxType);
    const giftBoxPrice = selectedBox?.price || 0;
    const giftBoxTotal = giftBoxPrice * (upsells.giftBoxQuantity || 0);

    // Warranty
    const warrantyTotal = upsells.hasWarranty
      ? (this.upsellProducts?.warranty?.price || 0)
      : 0;

    // Gift note
    const giftNoteTotal = upsells.giftNote?.trim()
      ? (this.upsellProducts?.giftNote?.price || 0)
      : 0;

    // Engraving
    const engravingTotal = engravingCount * BundleSummaryManager.ENGRAVING_PRICE;

    // Calculate totals
    const upsellsTotal = giftBoxTotal + warrantyTotal + giftNoteTotal + engravingTotal;
    const bundleDiscount = Math.max(0, originalPrice - BundleSummaryManager.BUNDLE_PRICE);
    const grandTotal = BundleSummaryManager.BUNDLE_PRICE + upsellsTotal;

    return {
      originalPrice,
      bundlePrice: BundleSummaryManager.BUNDLE_PRICE,
      bundleDiscount,
      upsells: {
        giftBoxTotal,
        warrantyTotal,
        giftNoteTotal,
        engravingTotal
      },
      upsellsTotal,
      grandTotal,
      totalSavings: bundleDiscount
    };
  }

  // ═══════════════════════════════════════════════════════════════
  // EVENT HANDLING
  // ═══════════════════════════════════════════════════════════════

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Remove existing handlers first
    this.cleanup();

    // Click handler
    this.boundClickHandler = (e) => {
      // Edit buttons
      const editBtn = e.target.closest('[data-bundle-edit]');
      if (editBtn) {
        const step = editBtn.dataset.bundleEdit;
        this.handleEdit(step);
        return;
      }

      // Back button
      if (e.target.closest('[data-bundle-back]')) {
        this.handleBack();
        return;
      }

      // Complete button
      if (e.target.closest('[data-bundle-complete]')) {
        this.handleComplete();
        return;
      }
    };
    this.container.addEventListener('click', this.boundClickHandler);
  }

  /**
   * Handle edit navigation
   */
  handleEdit(step) {
    this.stateManager.goToStep(step);
  }

  /**
   * Handle back button
   */
  handleBack() {
    this.stateManager.goToStep('upsells');
  }

  /**
   * Handle complete order button
   * Uses BundleCartManager for cart integration and checkout
   */
  handleComplete() {
    const cartManager = BundleCartManager.forState(
      this.stateManager,
      this.uiManager,
      this.upsellProducts
    );
    return cartManager.execute();
  }

  /**
   * Build cart items for Phase 8
   * Returns array of items with variant IDs and properties
   */
  buildCartItemsPreview(state) {
    const items = [];

    // Add the 3 bundle products
    state.products.forEach((product, index) => {
      const properties = {};

      // Pet names
      if (product.customization?.petNames?.length > 0) {
        product.customization.petNames.forEach((name, i) => {
          if (name && name.trim()) {
            properties[`Pet Name ${i + 1}`] = name;
          }
        });
      }

      // Photo URL
      if (product.customization?.photoUrl) {
        properties['Photo URL'] = product.customization.photoUrl;
      }

      // Engraving
      if (product.customization?.engraving) {
        Object.entries(product.customization.engraving).forEach(([pendant, data]) => {
          if (data.hasText && data.lines?.length > 0) {
            const text = data.lines.filter(l => l).join(' / ');
            properties[`Engraving Pendant ${pendant}`] = text;
          }
        });
      }

      // Add bundle identifier
      properties['_bundle_id'] = state.id;
      properties['_bundle_position'] = index + 1;

      items.push({
        id: product.selectedVariantId,
        quantity: 1,
        properties
      });
    });

    // Add gift boxes
    const upsells = state.upsells || {};
    if (upsells.giftBoxQuantity > 0) {
      const selectedBox = this.upsellProducts?.giftBoxes?.find(b => b.id === upsells.giftBoxType);
      if (selectedBox?.variantId) {
        items.push({
          id: selectedBox.variantId,
          quantity: upsells.giftBoxQuantity,
          properties: { '_bundle_id': state.id }
        });
      }
    }

    // Add warranty
    if (upsells.hasWarranty && this.upsellProducts?.warranty?.variantId) {
      items.push({
        id: this.upsellProducts.warranty.variantId,
        quantity: 1,
        properties: { '_bundle_id': state.id }
      });
    }

    // Add gift note product
    if (upsells.giftNote?.trim() && this.upsellProducts?.giftNote?.variantId) {
      items.push({
        id: this.upsellProducts.giftNote.variantId,
        quantity: 1,
        properties: {
          '_bundle_id': state.id,
          'Gift Note': upsells.giftNote
        }
      });
    }

    return items;
  }

  // ═══════════════════════════════════════════════════════════════
  // UTILITIES
  // ═══════════════════════════════════════════════════════════════

  /**
   * Format price from cents to display string
   */
  formatPrice(cents) {
    return BundleUpsellsManager.formatMoney(cents);
  }

  /**
   * Count engraved pendants that exist on the selected variant
   */
  static countEngravings(product) {
    const engraving = product.customization?.engraving || {};
    let count = 0;
    for (let i = 1; i <= Math.min(4, product.pendantCount || 1); i++) {
      if (engraving[i]?.hasText && engraving[i].lines?.some(line => line?.trim())) count++;
    }
    return count;
  }

  /**
   * Resize Shopify image URL
   */
  resizeImage(url, size) {
    if (!url) return '';

    // Handle Shopify CDN URLs
    if (url.includes('cdn.shopify.com')) {
      return url.replace(/(_\d+x\d+)?(\.[a-z]+)(\?.*)?$/i, `_${size}$2`);
    }

    // Handle file asset URLs
    if (url.includes('?')) {
      return `${url}&width=${size.split('x')[0]}`;
    }

    return url;
  }

  // ═══════════════════════════════════════════════════════════════
  // CLEANUP
  // ═══════════════════════════════════════════════════════════════

  /**
   * Cleanup event listeners
   */
  cleanup() {
    if (this.boundClickHandler) {
      this.container?.removeEventListener('click', this.boundClickHandler);
      this.boundClickHandler = null;
    }
  }
}

// Export for other modules
window.BundleSummaryManager = BundleSummaryManager;

// ═══════════════════════════════════════════════════════════════════════════════
// BUNDLE CART MANAGER - Phase 8
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * BundleCartManager - Handles cart integration for bundle checkout
 *
 * Responsibilities:
 * - Validate bundle state before checkout
 * - Build cart items array with all products + upsells
 * - Add engraving SERVICE products (separate line items)
 * - Submit to Shopify /cart/add.js with retry logic
 * - Handle errors with user feedback
 * - Redirect to checkout on success
 *
 * Cart Payload Structure:
 * - 3 main products (with Pet Name, Photo URL, Engraving text properties)
 * - 0-3 engraving service products (one per product with engraving)
 * - 0-1 gift box (with quantity 0-3)
 * - 0-1 warranty
 * - 0-1 gift note product (with 'Gift Note' property)
 */
class BundleCartManager {
  // ═══════════════════════════════════════════════════════════════
  // CONSTANTS
  // ═══════════════════════════════════════════════════════════════

  static ENGRAVING_HANDLE = 'optional-engraving-on-back';

  // Phase 9: User-initiated retry limits
  static USER_RETRY_MAX = 3; // Max times user can click "Try Again"
  static FETCH_TIMEOUT = 15000; // 15 second timeout for cart submission
  static transactions = new WeakMap();

  static forState(stateManager, uiManager, upsellProducts) {
    let manager = this.transactions.get(stateManager);
    if (!manager) {
      manager = new BundleCartManager(stateManager, uiManager, upsellProducts);
      this.transactions.set(stateManager, manager);
    }
    manager.uiManager = uiManager;
    manager.upsellProducts = upsellProducts;
    return manager;
  }

  static reviewError(code, message) {
    return Object.assign(new Error(message), { code, review: true });
  }

  static url(path) {
    return `${window.Shopify?.routes?.root || '/'}${path}`;
  }

  // ═══════════════════════════════════════════════════════════════
  // CONSTRUCTOR
  // ═══════════════════════════════════════════════════════════════

  constructor(stateManager, uiManager, upsellProducts) {
    this.stateManager = stateManager;
    this.uiManager = uiManager;
    this.upsellProducts = upsellProducts;
    this.button = null;
    this.originalButtonText = 'Complete Order';

    // Phase 9: Track user retry attempts
    this.userRetryCount = 0;
  }

  // ═══════════════════════════════════════════════════════════════
  // MAIN EXECUTION
  // ═══════════════════════════════════════════════════════════════

  /**
   * Execute the checkout flow
   * Called when user clicks "Complete Order"
   */
  async execute() {
    if (this.executing) return;
    this.button = document.querySelector('[data-bundle-complete]');
    if (!this.button) {
      // BUG-078: Add error logging for debug if button not found
      console.error('[BundleCartManager] Complete Order button not found');
      return;
    }

    // Store original text for reset (only from the idle button, never a state label)
    const stateClasses = ['bundle-btn-processing', 'bundle-btn-success', 'bundle-btn-error'];
    if (!stateClasses.some(name => this.button.classList.contains(name))) {
      this.originalButtonText = this.button.textContent;
    }
    this.executing = true;
    this.clearReviewNotice();

    try {
      // Step 1: Show processing state
      this.setButtonState('processing');

      // Step 2: Validate state
      const state = this.stateManager.get();
      const validation = this.validateState(state);
      if (!validation.valid) {
        this.handleValidationError(validation.error);
        return;
      }

      // Step 3: Build cart items (engraving variants are resolved by product handle)
      if (state.products.some(product => this.countProductEngravings(product.customization?.engraving, product.pendantCount || 1) > 0)) {
        await this.loadEngravingVariants();
      }
      const items = this.buildCartItems(state);

      // Step 4: Submit once; reconcile uncertain results before proceeding.
      await this.submitToCart(items);

      // Step 5: Success - clear state and redirect
      this.setButtonState('success');
      this.stateManager.clear();

      // Brief delay for visual feedback before redirect
      await this.delay(200);
      window.location.href = '/checkout';

    } catch (error) {
      this.handleError(error);
    } finally {
      this.executing = false;
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // VALIDATION
  // ═══════════════════════════════════════════════════════════════

  /**
   * Validate bundle state before checkout
   * @returns {{ valid: boolean, error?: string }}
   */
  validateState(state) {
    // Check 3 products
    if (!state.products || state.products.length !== 3) {
      return { valid: false, error: 'Please select 3 products for your bundle.' };
    }

    // Check each product has required data
    for (let i = 0; i < state.products.length; i++) {
      const product = state.products[i];

      if (!product.selectedVariantId) {
        return { valid: false, error: `Product ${i + 1} is missing a variant selection.` };
      }

      // BUG-080 FIX: Check pet name using requiresPetName (from pet-name tag), not pendantCount
      // This aligns checkout validation with step navigation validation
      if (product.requirements?.requiresPetName) {
        const petNames = product.customization?.petNames || [];
        const requiredNames = product.pendantCount || 1;

        // Each visible pendant needs its own name; names beyond the pendant count are hidden
        for (let n = 0; n < requiredNames; n++) {
          if (!petNames[n] || !String(petNames[n]).trim()) {
            return { valid: false, error: `Please enter all pet names for product ${i + 1}.` };
          }
        }
      }

      // Check photo if required
      if (product.requirements?.requiresPhoto && !product.customization?.photoUrl) {
        return { valid: false, error: `Please upload a photo for product ${i + 1}.` };
      }
    }

    return { valid: true };
  }

  /**
   * Handle validation error
   */
  handleValidationError(errorMessage) {
    this.setButtonState('error');
    this.uiManager.showToast(errorMessage, 'error');

    // Reset button after delay
    setTimeout(() => {
      this.setButtonState('default');
    }, 2000);
  }

  // ═══════════════════════════════════════════════════════════════
  // CART ITEM BUILDING
  // ═══════════════════════════════════════════════════════════════

  /**
   * Build complete cart items array
   * @returns {Array} Items for /cart/add.js
   */
  buildCartItems(state) {
    const items = [];

    // Add the 3 bundle products
    state.products.forEach((product, index) => {
      // Main product item
      items.push({
        id: product.selectedVariantId,
        quantity: 1,
        properties: this.buildProductProperties(product, index + 1, state.id)
      });

      // Engraving SERVICE product (if product has engraving)
      const engravingItem = this.buildEngravingServiceItem(product, state.id);
      if (engravingItem) {
        items.push(engravingItem);
      }
    });

    // Add upsells
    const upsells = state.upsells || {};

    // Gift boxes
    if (upsells.giftBoxQuantity > 0) {
      const selectedBox = this.upsellProducts?.giftBoxes?.find(b => b.id === upsells.giftBoxType);
      if (selectedBox?.variantId) {
        items.push({
          id: selectedBox.variantId,
          quantity: upsells.giftBoxQuantity,
          properties: {
            '_bundle': 'Buy 3 for $99',
            '_bundle_id': state.id
          }
        });
      }
    }

    // Warranty
    if (upsells.hasWarranty && this.upsellProducts?.warranty?.variantId) {
      items.push({
        id: this.upsellProducts.warranty.variantId,
        quantity: 1,
        properties: {
          '_bundle': 'Buy 3 for $99',
          '_bundle_id': state.id
        }
      });
    }

    // Gift note product (with Gift Note property for fulfillment visibility)
    if (upsells.giftNote?.trim() && this.upsellProducts?.giftNote?.variantId) {
      items.push({
        id: this.upsellProducts.giftNote.variantId,
        quantity: 1,
        properties: {
          '_bundle': 'Buy 3 for $99',
          '_bundle_id': state.id,
          'Gift Note': upsells.giftNote.trim()
        }
      });
    }

    return items;
  }

  /**
   * Build properties object for a main product
   */
  buildProductProperties(product, position, bundleId) {
    const properties = {
      '_bundle': 'Buy 3 for $99',
      '_bundle_id': bundleId,
      '_bundle_position': position
    };

    // Pet names - using "Pet's name", "Pet's name 2", etc. format for fulfillment
    // Names beyond the selected variant's pendant count are stale and not sent.
    const petNames = (product.customization?.petNames || []).slice(0, product.pendantCount || 1);
    // BUG-069 FIX: Respect noCaps setting for text transformation
    const noCaps = product.requirements?.noCaps;
    petNames.forEach((name, i) => {
      if (name && name.trim()) {
        // First pet name is "Pet's name", subsequent are "Pet's name 2", etc.
        const key = i === 0 ? "Pet's name" : `Pet's name ${i + 1}`;
        // NoCaps: Use Title Case (first letter uppercase, rest lowercase)
        // Default: ALL CAPS
        if (noCaps) {
          const trimmed = name.trim();
          properties[key] = trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
        } else {
          properties[key] = name.trim().toUpperCase();
        }
      }
    });

    // Photo URL
    if (product.customization?.photoUrl) {
      properties['Photo URL'] = product.customization.photoUrl;
    }

    // Engraving TEXT (stored on main product for reference)
    // Format: "Back Engraving" for single pendant, "Back Engraving 1", "Back Engraving 2" for multi
    const engraving = product.customization?.engraving || {};
    const pendantCount = product.pendantCount || 1;

    if (pendantCount === 1) {
      // Single pendant - use "Back Engraving" without number
      const pendant = engraving[1];
      if (pendant?.hasText && pendant.lines?.length > 0) {
        const text = this.formatEngravingText(pendant.lines);
        if (text) {
          properties['Back Engraving'] = text;
        }
      }
    } else {
      // Multiple pendants - use "Back Engraving 1", "Back Engraving 2", etc.
      for (let i = 1; i <= pendantCount; i++) {
        const pendant = engraving[i];
        if (pendant?.hasText && pendant.lines?.length > 0) {
          const text = this.formatEngravingText(pendant.lines);
          if (text) {
            properties[`Back Engraving ${i}`] = text;
          }
        }
      }
    }

    return properties;
  }

  /**
   * Build engraving SERVICE product item
   * Returns null if product has no engraving
   */
  buildEngravingServiceItem(product, bundleId) {
    const engravingCount = this.countProductEngravings(product.customization?.engraving, product.pendantCount || 1);

    if (engravingCount === 0) {
      return null;
    }

    // Determine variant based on pendant size
    const variantId = this.getEngravingVariantId(product.pendantSize);

    return {
      id: variantId,
      quantity: engravingCount,
      properties: {
        '_bundle': 'Buy 3 for $99',
        '_bundle_id': bundleId,
        '_parent_product': product.productTitle
      }
    };
  }

  /**
   * Count how many pendants have engraving text
   */
  countProductEngravings(engraving, pendantCount = 4) {
    if (!engraving) return 0;

    let count = 0;
    for (let i = 1; i <= Math.min(4, pendantCount); i++) {
      const pendant = engraving[i];
      if (pendant?.hasText && pendant.lines?.some(line => line?.trim())) {
        count++;
      }
    }
    return count;
  }

  /**
   * Format engraving lines with || separator
   */
  formatEngravingText(lines) {
    if (!lines || !Array.isArray(lines)) return '';
    return lines.filter(line => line?.trim()).join(' || ');
  }

  /**
   * Load the engraving service variants from this store's product handle
   */
  async loadEngravingVariants() {
    const response = await BundleStateManager.fetchWithTimeout(
      BundleCartManager.url(`products/${BundleCartManager.ENGRAVING_HANDLE}.js`),
      { headers: { 'Accept': 'application/json' } },
      BundleCartManager.FETCH_TIMEOUT
    );
    if (!response.ok) {
      throw new Error(response.status === 404 ? 'ENGRAVING_UNAVAILABLE' : `HTTP ${response.status}`);
    }
    const product = await response.json();
    this.engravingVariants = Array.isArray(product?.variants) ? product.variants : [];
  }

  /**
   * Get engraving variant ID based on pendant size
   */
  getEngravingVariantId(pendantSize) {
    // 2.0cm pendants use the "Large Pendant" variant, everything else "Standard"
    const title = pendantSize?.includes('2.0') ? 'Large Pendant' : 'Standard';
    const variant = this.engravingVariants?.find(v => v.title === title);
    if (!variant?.id || !variant.available) {
      throw new Error('ENGRAVING_UNAVAILABLE');
    }
    return variant.id;
  }

  // ═══════════════════════════════════════════════════════════════
  // CART SUBMISSION
  // ═══════════════════════════════════════════════════════════════

  /**
   * Submit items to cart once
   * A failed or timed-out add is checked against /cart.js before anything is
   * sent again, so neither a retry nor a reload can add the same bundle twice.
   */
  async submitToCart(items) {
    const bundleId = items[0]?.properties?._bundle_id;
    const selection = JSON.stringify(items);

    if (this.confirmed?.bundleId === bundleId) {
      if (this.confirmed.selection !== selection) throw this.previousConfirmedError();
      return null;
    }

    let cart = null;
    try {
      cart = await this.readCart();
    } catch (_) {
      // Without an earlier uncertain add, an unreadable cart does not block the first add.
      if (this.uncertain?.bundleId === bundleId) {
        throw BundleCartManager.reviewError('UNCERTAIN', 'We could not confirm the cart change. Please review your cart before trying again.');
      }
    }
    if (cart) {
      const status = this.bundleCartStatus(cart, items);
      if (status === 'complete') return this.confirm(bundleId, selection, cart);
      if (status === 'partial') {
        const earlier = this.uncertain?.bundleId === bundleId ? this.uncertain : null;
        if (earlier && this.bundleCartStatus(cart, earlier.items) === 'complete') {
          this.confirm(bundleId, earlier.selection, cart);
          throw this.previousConfirmedError();
        }
        throw this.uncertainError();
      }
    }

    this.uncertain = { bundleId, selection, items };
    let response = null;
    let failure = null;
    try {
      // Phase 9: Use fetchWithTimeout instead of native fetch
      response = await BundleStateManager.fetchWithTimeout(
        BundleCartManager.url('cart/add.js'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ items })
        },
        BundleCartManager.FETCH_TIMEOUT
      );
    } catch (error) {
      failure = error;
    }

    if (response?.ok) {
      return this.confirm(bundleId, selection, await response.json().catch(() => null));
    }

    // Not confirmed: check what actually reached the cart before reporting.
    const errorData = response ? await response.json().catch(() => ({})) : {};
    const after = await this.readCart().catch(() => null);
    if (after) {
      const status = this.bundleCartStatus(after, items);
      if (status === 'complete') return this.confirm(bundleId, selection, after);
      if (status === 'partial') throw this.uncertainError();
      if (response && response.status < 500) this.uncertain = null;
    }

    if (failure) {
      // Phase 9: Transform timeout error to identifiable type
      if (failure.name === 'TimeoutError' || failure.message === 'NETWORK_TIMEOUT') {
        throw new Error('NETWORK_TIMEOUT');
      }
      throw failure;
    }
    if (response.status === 422) {
      // Item unavailable/out of stock
      throw new Error('ITEM_UNAVAILABLE');
    }
    throw new Error(errorData.description || `HTTP ${response.status}`);
  }

  /**
   * Read the current cart
   */
  async readCart() {
    const response = await BundleStateManager.fetchWithTimeout(
      BundleCartManager.url('cart.js'),
      { cache: 'no-store', headers: { 'Accept': 'application/json' } },
      BundleCartManager.FETCH_TIMEOUT
    );
    const cart = response.ok ? await response.json() : null;
    if (!Array.isArray(cart?.items)) throw new Error('CART_READ_FAILED');
    return cart;
  }

  /**
   * Compare the cart lines of this bundle with the items that were sent
   * @returns {'none'|'partial'|'complete'}
   */
  bundleCartStatus(cart, items) {
    const bundleId = String(items[0]?.properties?._bundle_id);
    const lines = cart.items.filter(line => String(line.properties?._bundle_id) === bundleId);
    if (lines.length === 0) return 'none';

    const groups = new Map();
    items.forEach(item => {
      const properties = Object.entries(item.properties || {}).map(([key, value]) => [key, String(value)]);
      const key = JSON.stringify([Number(item.id), properties.sort(([a], [b]) => a.localeCompare(b))]);
      const group = groups.get(key) || { id: Number(item.id), properties, quantity: 0 };
      group.quantity += Number(item.quantity);
      groups.set(key, group);
    });
    // Automatic discounts can split one requested item into several lines.
    const quantity = list => list.reduce((total, line) => total + Number(line.quantity), 0);
    const complete = [...groups.values()].every(group => quantity(lines.filter(line =>
      Number(line.variant_id) === group.id &&
      group.properties.every(([key, value]) => String(line.properties?.[key]) === value))) === group.quantity);
    return complete && quantity(lines) === quantity(items) ? 'complete' : 'partial';
  }

  confirm(bundleId, selection, result) {
    this.confirmed = { bundleId, selection };
    this.uncertain = null;
    return result;
  }

  uncertainError() {
    return BundleCartManager.reviewError('UNCERTAIN', 'We could not confirm every item was added. Please review your cart before trying again.');
  }

  previousConfirmedError() {
    return BundleCartManager.reviewError('PREVIOUS_CONFIRMED', 'Your previous bundle was added. Your edited selection has not been added. Please review the earlier bundle in your cart.');
  }

  // ═══════════════════════════════════════════════════════════════
  // BUTTON STATE MANAGEMENT
  // ═══════════════════════════════════════════════════════════════

  /**
   * Set button visual state
   * @param {'default'|'processing'|'success'|'error'} state
   */
  setButtonState(state) {
    if (!this.button) return;

    // Remove all state classes
    this.button.classList.remove(
      'bundle-btn-processing',
      'bundle-btn-success',
      'bundle-btn-error'
    );
    document.body.classList.remove('bundle-checkout-processing');

    switch (state) {
      case 'processing':
        this.button.disabled = true;
        this.button.classList.add('bundle-btn-processing');
        this.button.innerHTML = `
          <span class="spinner-border spinner-border-sm me-2" role="status"></span>
          Processing...
        `;
        document.body.classList.add('bundle-checkout-processing');
        break;

      case 'success':
        this.button.disabled = true;
        this.button.classList.add('bundle-btn-success');
        this.button.innerHTML = `
          <span class="me-2">✓</span>
          Redirecting...
        `;
        break;

      case 'error':
        this.button.disabled = true;
        this.button.classList.add('bundle-btn-error');
        this.button.innerHTML = `
          <span class="me-2">✕</span>
          Error
        `;
        break;

      default:
        this.button.disabled = false;
        this.button.textContent = this.originalButtonText;
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // ERROR HANDLING - Phase 9 Enhanced
  // ═══════════════════════════════════════════════════════════════

  /**
   * Handle checkout errors
   * Phase 9: Shows retry button UI instead of just toast
   */
  handleError(error) {
    // The cart may already hold this bundle: send the customer to review it, never resend.
    if (error.review) {
      this.setButtonState('default');
      this.showReviewNotice(error.message);
      return;
    }

    this.setButtonState('error');

    // Increment user retry count
    this.userRetryCount++;

    // Determine error message
    let message = 'Something went wrong. Please try again.';
    let isRecoverable = true;

    if (error.message === 'ITEM_UNAVAILABLE') {
      message = 'One or more items are no longer available. Please review your selections.';
      isRecoverable = false; // OOS requires product change, not retry
    } else if (error.message === 'ENGRAVING_UNAVAILABLE') {
      message = 'Back engraving is temporarily unavailable. Please remove that selection or contact us.';
      isRecoverable = false;
    } else if (error.message === 'NETWORK_TIMEOUT') {
      message = 'Request timed out. Please check your connection and try again.';
    } else if (!navigator.onLine) {
      message = 'No internet connection. Please check your connection and try again.';
    }

    // Phase 9: Show retry UI if recoverable and under retry limit
    if (isRecoverable && this.userRetryCount <= BundleCartManager.USER_RETRY_MAX) {
      this.showRetryButton(message);
    } else if (isRecoverable) {
      // Exhausted retries
      this.showPersistentError();
    } else {
      // Non-recoverable error (OOS) - show toast and reset nav
      this.uiManager.showToast(message, 'error');
      this.resetNavigation();
    }
  }

  /**
   * Show retry button UI with error message
   * Phase 9: User can click to retry without refreshing page
   *
   * @param {string} message - Error message to display
   */
  showRetryButton(message) {
    const nav = this.button?.closest('.bundle-summary-nav');
    if (!nav) return;

    // Store reference for cleanup
    const container = document.createElement('div');
    container.className = 'bundle-checkout-error';
    container.innerHTML = `
      <div class="alert alert-danger mb-3" role="alert">
        <strong>Error:</strong> ${message}
      </div>
      <div class="bundle-retry-actions">
        <button type="button" class="btn btn-outline-secondary" data-bundle-back-to-summary>
          ← Back
        </button>
        <button type="button" class="btn btn-primary" data-bundle-retry>
          Try Again (${BundleCartManager.USER_RETRY_MAX - this.userRetryCount + 1} left)
        </button>
      </div>
    `;

    // Replace navigation with retry UI
    nav.style.display = 'none';
    nav.parentNode.insertBefore(container, nav.nextSibling);

    // Bind retry click handler
    container.querySelector('[data-bundle-retry]').addEventListener('click', () => {
      container.remove();
      nav.style.display = '';
      this.setButtonState('default');
      this.retryCheckout();
    });

    // Bind back button handler
    container.querySelector('[data-bundle-back-to-summary]').addEventListener('click', () => {
      container.remove();
      nav.style.display = '';
      this.setButtonState('default');
    });
  }

  /**
   * Show a notice asking the customer to review the cart
   */
  showReviewNotice(message) {
    const nav = this.button?.closest('.bundle-summary-nav');
    if (!nav) {
      this.uiManager.showToast(message, 'error');
      return;
    }
    const notice = document.createElement('p');
    notice.className = 'alert alert-danger mt-3';
    notice.setAttribute('role', 'alert');
    notice.dataset.bundleReviewNotice = '';
    const link = document.createElement('a');
    link.href = BundleCartManager.url('cart');
    link.textContent = 'View cart';
    notice.append(`${message} `, link);
    nav.after(notice);
  }

  clearReviewNotice() {
    this.button?.closest('.bundle-summary-nav')?.parentNode?.querySelector('[data-bundle-review-notice]')?.remove();
  }

  /**
   * Show persistent error state (after max retries exhausted)
   * Phase 9: Offers refresh as last resort
   */
  showPersistentError() {
    const nav = this.button?.closest('.bundle-summary-nav');
    if (!nav) return;

    const container = document.createElement('div');
    container.className = 'bundle-persistent-error';
    container.innerHTML = `
      <div class="alert alert-danger mb-3" role="alert">
        <strong>Unable to complete your order.</strong><br>
        We're having trouble connecting. Please try refreshing the page.
      </div>
      <button type="button" class="btn btn-primary w-100" onclick="window.location.reload()">
        Refresh Page
      </button>
      <button type="button" class="btn btn-outline-secondary w-100" data-bundle-back-to-summary>
        Back to Summary
      </button>
    `;

    nav.style.display = 'none';
    nav.parentNode.insertBefore(container, nav.nextSibling);

    // Back button resets everything
    container.querySelector('[data-bundle-back-to-summary]').addEventListener('click', () => {
      container.remove();
      nav.style.display = '';
      this.setButtonState('default');
      this.userRetryCount = 0; // Reset retry count
    });
  }

  /**
   * Retry the checkout from the current bundle state
   * submitToCart checks /cart.js first, so a retry never adds the bundle twice.
   */
  retryCheckout() {
    return this.execute();
  }

  /**
   * Reset navigation buttons to default state
   * Phase 9: Helper for non-recoverable errors
   */
  resetNavigation() {
    setTimeout(() => {
      this.setButtonState('default');
    }, 3000);
  }

  // ═══════════════════════════════════════════════════════════════
  // UTILITIES
  // ═══════════════════════════════════════════════════════════════

  /**
   * Promise-based delay
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Export for other modules
window.BundleCartManager = BundleCartManager;
